import React from 'react';
import {
  Clock,
  ArrowRight,
  FileText
} from "lucide-react";
import { Link } from "react-router-dom";

// Simple components to replace Base44 imports
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="px-6 py-4 border-b">
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Badge = ({ children, className = "", variant = "default" }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${className}`}>
    {children}
  </span>
);

const Button = ({ children, className = "", variant = "default", size = "default", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-lg font-medium transition-colors";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50"
  };
  const sizes = {
    default: "px-4 py-2",
    sm: "px-3 py-1.5 text-sm"
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Simple functions to replace Base44 utilities
const createPageUrl = (page) => `/${page.toLowerCase()}`;
const format = (date, formatStr) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

export default function RecentVisits({ visits }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'Missed': return 'bg-red-100 text-red-800 border-red-200';
      case 'Planned': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Rescheduled': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Recent Visits
          </CardTitle>
          <Link to={createPageUrl("Reports")}>
            <Button variant="outline" size="sm" className="gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {visits.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No recent visits</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visits.map((visit) => (
              <div key={visit.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-900">{visit.doctor_name}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {format(new Date(visit.visit_date), 'MMM d, yyyy')}
                      </div>
                      {visit.visit_time && (
                        <span>{visit.visit_time}</span>
                      )}
                    </div>
                  </div>
                  <Badge className={getStatusColor(visit.status)}>
                    {visit.status}
                  </Badge>
                </div>
                {visit.notes && (
                  <p className="text-sm text-gray-600 mt-2">{visit.notes}</p>
                )}
                {visit.products_promoted && visit.products_promoted.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Products Promoted:</p>
                    <div className="flex gap-1 flex-wrap">
                      {visit.products_promoted.slice(0, 2).map((product, index) => (
                        <Badge key={index} className="text-xs bg-gray-100 text-gray-700">
                          {product}
                        </Badge>
                      ))}
                      {visit.products_promoted.length > 2 && (
                        <Badge className="text-xs bg-gray-100 text-gray-700">
                          +{visit.products_promoted.length - 2} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
